import {
	DeleteOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Skeleton,
	Space,
	Tabs,
	Typography,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomNotification } from 'src/components/custom-notification';
import EditorComp from 'src/components/ui/editor';
import MediaUpload from 'src/components/ui/media-upload';
import { Media, MediaFoldersNames } from 'src/lib/interface';
import { MutationUpsertStaticPage } from 'src/lib/gql/mutations';
import { QueryGetStaticPage } from 'src/lib/gql/queries';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SLUG = 'about-us';

// ---------------------------------------------------------------------------
// Local type definitions (will be auto-generated once backend schema is ready)
// ---------------------------------------------------------------------------
interface StaticPageMedia {
	id: string;
	url?: string | null;
}
interface StaticPageValue {
	id?: string;
	number?: string;
	title?: string;
	description?: string;
	sortOrder?: number;
}
interface StaticPageTimeline {
	id?: string;
	year?: string;
	label?: string;
	description?: string;
	sortOrder?: number;
	image?: StaticPageMedia | null;
}
interface StaticPageEditor {
	id?: string;
	name?: string;
	position?: string;
	bio?: string;
	sortOrder?: number;
	photo?: StaticPageMedia | null;
}
interface StaticPageCity {
	id?: string;
	name?: string;
	url?: string;
	sortOrder?: number;
	backgroundImage?: StaticPageMedia | null;
	logo?: StaticPageMedia | null;
}
interface StaticPageData {
	id?: string;
	heroTitle?: string;
	heroSubtitle?: string;
	heroImage?: StaticPageMedia | null;
	aboutTitle?: string;
	aboutBody?: string;
	teamPhoto?: StaticPageMedia | null;
	values?: StaticPageValue[];
	timeline?: StaticPageTimeline[];
	editors?: StaticPageEditor[];
	cities?: StaticPageCity[];
	metaTitle?: string;
	metaDescription?: string;
}

// ---------------------------------------------------------------------------
// Repeater helpers
// ---------------------------------------------------------------------------
function newValue(): StaticPageValue {
	return { number: '', title: '', description: '', sortOrder: 0 };
}
function newTimeline(): StaticPageTimeline {
	return { year: '', label: '', description: '', sortOrder: 0, image: null };
}
function newEditor(): StaticPageEditor {
	return { name: '', position: '', sortOrder: 0, photo: null };
}
function newCity(): StaticPageCity {
	return { name: '', url: '', sortOrder: 0, backgroundImage: null, logo: null };
}

// ---------------------------------------------------------------------------
// Default content — mirrors the website's FALLBACK_ constants.
// Used as initial state so saving once commits real data to the DB instead
// of empty arrays that cause the website to fall back to hardcoded content.
// ---------------------------------------------------------------------------
const DEFAULT_VALUES: StaticPageValue[] = [
	{ number: '01', title: 'On The Ground', sortOrder: 0, description: "We don't rely on second hand information. DubaiNight is built on real presence, with our team out daily across restaurants, bars, beach clubs, and events in Dubai, capturing what is actually happening in the city." },
	{ number: '02', title: 'Real Coverage', sortOrder: 1, description: "We focus on showing places as they are. No overproduction, no distortion. Whether it's a new opening or an established venue, our content reflects the real atmosphere, people, and energy behind Dubai's nightlife and lifestyle scene." },
	{ number: '03', title: 'Consistency', sortOrder: 2, description: "Dubai moves fast, and so do we. Through daily content, continuous coverage, and regular updates, DubaiNight remains a reliable source for discovering where to go, what to do, and what's new across the city." },
	{ number: '04', title: 'Supporting The Scene', sortOrder: 3, description: "DubaiNight exists because of the venues, teams, and people behind the city. From restaurants and beach clubs to events and cultural spaces, we use our platform to drive visibility and support the community that shapes Dubai's nightlife and lifestyle." },
];

const DEFAULT_TIMELINE: StaticPageTimeline[] = [
	{ year: '2012', label: 'Launch', sortOrder: 0, image: null, description: "DubaiNight was launched in 2012 with a clear focus: to capture and share Dubai's nightlife and dining scene as it was happening." },
	{ year: '2020', label: 'DBN Media Group', sortOrder: 1, image: null, description: 'As DubaiNight grew, it naturally expanded into DBN Media Group, extending its capabilities beyond editorial into production and events.' },
	{ year: '2025', label: 'Global Expansion', sortOrder: 2, image: null, description: 'Building on its presence in Dubai, DubaiNight expanded internationally in 2025, launching in key destinations including Beirut, Riyadh, Egypt, Ibiza, and Mykonos.' },
];

const DEFAULT_EDITORS: StaticPageEditor[] = [
	{ name: '', position: '', sortOrder: 0, photo: null },
	{ name: '', position: '', sortOrder: 1, photo: null },
];

const DEFAULT_CITIES: StaticPageCity[] = [
	{ name: 'AbudhabiNight', url: 'https://abudhabinight.com/', sortOrder: 0, backgroundImage: null, logo: null },
	{ name: 'RiyadhNight', url: 'https://saudinights.com/riyadh/', sortOrder: 1, backgroundImage: null, logo: null },
	{ name: 'EgyptNight', url: 'https://egyptnight.com', sortOrder: 2, backgroundImage: null, logo: null },
	{ name: 'BeirutNight', url: 'https://beirutnight.com', sortOrder: 3, backgroundImage: null, logo: null },
	{ name: 'IbizaNight', url: 'https://ibizanight.com', sortOrder: 4, backgroundImage: null, logo: null },
	{ name: 'MykonosNight', url: 'https://mykonosnight.com', sortOrder: 5, backgroundImage: null, logo: null },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AboutUsEditor: React.FC = () => {
	const [form] = useForm();

	// Hero image
	const [heroMedia, setHeroMedia] = useState<Media | undefined>(undefined);
	// Team photo (About section)
	const [teamPhotoMedia, setTeamPhotoMedia] = useState<Media | undefined>(undefined);

	// Repeater states — pre-populated with defaults so first save commits real content
	const [values, setValues] = useState<StaticPageValue[]>(DEFAULT_VALUES);
	const [timeline, setTimeline] = useState<StaticPageTimeline[]>(DEFAULT_TIMELINE);
	const [editors, setEditors] = useState<StaticPageEditor[]>(DEFAULT_EDITORS);
	const [cities, setCities] = useState<StaticPageCity[]>(DEFAULT_CITIES);

	// Media IDs for repeater items — sized to match the default arrays above
	const [timelineImages, setTimelineImages] = useState<(Media | undefined)[]>(DEFAULT_TIMELINE.map(() => undefined));
	const [editorPhotos, setEditorPhotos] = useState<(Media | undefined)[]>(DEFAULT_EDITORS.map(() => undefined));
	const [cityBgImages, setCityBgImages] = useState<(Media | undefined)[]>(DEFAULT_CITIES.map(() => undefined));
	const [cityLogos, setCityLogos] = useState<(Media | undefined)[]>(DEFAULT_CITIES.map(() => undefined));

	// ---------------------------------------------------------------------------
	// Query
	// ---------------------------------------------------------------------------
	const { data: qryData, loading, error: queryError } = useQuery<{ getStaticPageBySlug: StaticPageData }>(
		QueryGetStaticPage,
		{
			variables: { slug: SLUG },
			onError: () => {
				// Page may not exist yet — that is fine, we will create it on first save.
			},
		},
	);

	// Detect a missing-table DB error caused by a pending API migration.
	const isDbMigrationPending = queryError?.graphQLErrors?.some(
		(e) => e.message?.includes('does not exist'),
	) ?? false;

	useEffect(() => {
		const page = qryData?.getStaticPageBySlug;
		if (!page) return;

		// Populate flat fields via form
		form.setFieldsValue({
			heroTitle: page.heroTitle,
			heroSubtitle: page.heroSubtitle,
			aboutTitle: page.aboutTitle,
			aboutBody: page.aboutBody,
			metaTitle: page.metaTitle,
			metaDescription: page.metaDescription,
		});

		if (page.heroImage) {
			setHeroMedia({ id: page.heroImage.id, url: page.heroImage.url ?? undefined });
		}
		if (page.teamPhoto) {
			setTeamPhotoMedia({ id: page.teamPhoto.id, url: page.teamPhoto.url ?? undefined });
		}

		if (page.values?.length) setValues(page.values);
		if (page.timeline?.length) {
			setTimeline(page.timeline);
			setTimelineImages(page.timeline.map((t) =>
				t.image ? { id: t.image.id, url: t.image.url ?? undefined } : undefined,
			));
		}
		if (page.editors?.length) {
			setEditors(page.editors);
			setEditorPhotos(page.editors.map((e) =>
				e.photo ? { id: e.photo.id, url: e.photo.url ?? undefined } : undefined,
			));
		}
		if (page.cities?.length) {
			setCities(page.cities);
			setCityBgImages(page.cities.map((c) =>
				c.backgroundImage ? { id: c.backgroundImage.id, url: c.backgroundImage.url ?? undefined } : undefined,
			));
			setCityLogos(page.cities.map((c) =>
				c.logo ? { id: c.logo.id, url: c.logo.url ?? undefined } : undefined,
			));
		}
	}, [qryData, form]);

	// ---------------------------------------------------------------------------
	// Mutation
	// ---------------------------------------------------------------------------
	const [upsertPage, { loading: saving }] = useMutation(MutationUpsertStaticPage, {
		onError: (error) => {
			CustomNotification({
				pageName: 'About Us',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const handleSave = async () => {
		const formValues = form.getFieldsValue();

		const payload = {
			heroTitle: formValues.heroTitle,
			heroSubtitle: formValues.heroSubtitle,
			heroImageId: heroMedia?.id,
			aboutTitle: formValues.aboutTitle,
			aboutBody: formValues.aboutBody,
			teamPhotoId: teamPhotoMedia?.id,
			metaTitle: formValues.metaTitle,
			metaDescription: formValues.metaDescription,

			values: values
				.filter((v) => v.number || v.title || v.description)
				.map((v, i) => ({
					number: v.number,
					title: v.title,
					description: v.description,
					sortOrder: i,
				})),

			timeline: timeline
				.filter((t) => t.year || t.label || t.description)
				.map((t, i) => ({
					year: t.year,
					label: t.label,
					description: t.description,
					sortOrder: i,
					imageId: timelineImages[timeline.indexOf(t)]?.id,
				})),

		editors: editors
			.filter((e, i) => e.name || e.position || e.bio || editorPhotos[i]?.id)
			.map((e, i) => ({
				name: e.name,
				position: e.position,
				bio: e.bio,
				sortOrder: i,
				photoId: editorPhotos[editors.indexOf(e)]?.id,
			})),

			cities: cities
				.filter((c, i) => c.name || c.url || cityBgImages[i]?.id || cityLogos[i]?.id)
				.map((c, i) => ({
					name: c.name,
					url: c.url,
					sortOrder: i,
					backgroundImageId: cityBgImages[cities.indexOf(c)]?.id,
					logoId: cityLogos[cities.indexOf(c)]?.id,
				})),
		};

		const rsp = await upsertPage({ variables: { slug: SLUG, data: payload } });
		if (rsp.data) {
			CustomNotification({
				pageName: 'About Us',
				pagePrefix: 'Static Page',
				notificationType: 'updated',
			});
		}
	};

	// ---------------------------------------------------------------------------
	// Repeater helpers
	// ---------------------------------------------------------------------------
	const addRepeaterItem = useCallback(
		<T,>(
			setter: React.Dispatch<React.SetStateAction<T[]>>,
			mediaSetter: React.Dispatch<React.SetStateAction<(Media | undefined)[]>> | null,
			factory: () => T,
		) => {
			setter((prev) => [...prev, factory()]);
			if (mediaSetter) mediaSetter((prev) => [...prev, undefined]);
		},
		[],
	);

	const removeRepeaterItem = useCallback(
		<T,>(
			index: number,
			setter: React.Dispatch<React.SetStateAction<T[]>>,
			mediaSetter: React.Dispatch<React.SetStateAction<(Media | undefined)[]>> | null,
		) => {
			setter((prev) => prev.filter((_, i) => i !== index));
			if (mediaSetter) mediaSetter((prev) => prev.filter((_, i) => i !== index));
		},
		[],
	);

	const updateRepeaterField = useCallback(
		<T,>(
			index: number,
			field: keyof T,
			value: unknown,
			setter: React.Dispatch<React.SetStateAction<T[]>>,
		) => {
			setter((prev) =>
				prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
			);
		},
		[],
	);

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------
	if (loading) return <Skeleton active />;

	if (isDbMigrationPending) {
		return (
			<Alert
				type="warning"
				showIcon
				message="About Us editor is temporarily unavailable"
				description="The API database migration for the staticPage model has not yet been applied on this environment. Please run the pending migration on the API side and redeploy before using this editor."
				style={{ marginTop: 24 }}
			/>
		);
	}

	return (
		<>
			<Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
				<Col>
					<Title level={3} style={{ margin: 0 }}>
						About Us — Page Editor
					</Title>
					<Text type="secondary">Region-specific content saved via API.</Text>
				</Col>
				<Col>
					<Button
						className="btn-main save"
						loading={saving}
						onClick={handleSave}
						size="large"
					>
						Save All Changes
					</Button>
				</Col>
			</Row>

			<Tabs defaultActiveKey="hero" type="card">
				{/* ---------------------------------------------------------------- */}
				{/* TAB: HERO                                                         */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab="Hero" key="hero">
					<Card>
						<div className="form-field-block">
							<label className="input-fields-label">Hero Background Image</label>
							<MediaUpload
								folder={MediaFoldersNames.misc}
								accept="IMAGE"
								media={heroMedia}
								height="240px"
								objectFit="cover"
								placeholderText="Drag and drop the hero background image"
								onMediaUploaded={(m) => setHeroMedia(m)}
							/>
						</div>
						<Form form={form} component={false}>
							<div className="form-field-block">
								<label className="input-fields-label">Hero Title</label>
								<Form.Item name="heroTitle">
									<Input
										className="input-fields"
										placeholder="e.g. The Night Life Experts"
									/>
								</Form.Item>
							</div>
							<div className="form-field-block">
								<label className="input-fields-label">Hero Subtitle</label>
								<Form.Item name="heroSubtitle">
									<Input.TextArea
										className="input-fields"
										rows={3}
										placeholder="Short tagline under the title"
									/>
								</Form.Item>
							</div>
						</Form>
					</Card>
				</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: ABOUT / STORY                                                */}
				{/* ---------------------------------------------------------------- */}
			<TabPane tab="About / Story" key="about">
				<Card>
					<div className="form-field-block">
						<label className="input-fields-label">Team Photo</label>
						<MediaUpload
							folder={MediaFoldersNames.misc}
							accept="IMAGE"
							media={teamPhotoMedia}
							height="180px"
							maxWidth={480}
							objectFit="cover"
							placeholderText="Upload the team photo shown in the About section"
							onMediaUploaded={(m) => setTeamPhotoMedia(m)}
						/>
					</div>
					<Form form={form} component={false}>
						<div className="form-field-block">
							<label className="input-fields-label">Section Title</label>
							<Form.Item name="aboutTitle">
								<Input
									className="input-fields"
									placeholder="e.g. Our Story"
								/>
							</Form.Item>
						</div>
						<div className="form-field-block">
							<label className="input-fields-label">Body Content (Rich Text)</label>
							<Form.Item name="aboutBody" initialValue="">
								<EditorComp value="" />
							</Form.Item>
						</div>
					</Form>
				</Card>
			</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: VALUES                                                       */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab={`Values (${values.length})`} key="values">
					<Space direction="vertical" style={{ width: '100%' }} size="middle">
						{values.map((val, idx) => (
							<Card
								key={idx}
								title={`Value ${idx + 1}`}
								extra={
									<Button
										danger
										icon={<DeleteOutlined />}
										size="small"
										onClick={() => removeRepeaterItem(idx, setValues, null)}
										disabled={values.length <= 1}
									>
										Remove
									</Button>
								}
							>
								<Row gutter={16}>
									<Col span={24} md={4}>
										<div className="form-field-block">
											<label className="input-fields-label">Number / Icon</label>
											<Input
												className="input-fields"
												placeholder="e.g. 01"
												value={val.number}
												onChange={(e) =>
													updateRepeaterField(idx, 'number', e.target.value, setValues)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={8}>
										<div className="form-field-block">
											<label className="input-fields-label">Title</label>
											<Input
												className="input-fields"
												placeholder="e.g. Years of Experience"
												value={val.title}
												onChange={(e) =>
													updateRepeaterField(idx, 'title', e.target.value, setValues)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={12}>
										<div className="form-field-block">
											<label className="input-fields-label">Description</label>
											<Input.TextArea
												className="input-fields"
												rows={2}
												placeholder="Short description"
												value={val.description}
												onChange={(e) =>
													updateRepeaterField(idx, 'description', e.target.value, setValues)
												}
											/>
										</div>
									</Col>
								</Row>
							</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							className="btn-main"
							onClick={() => addRepeaterItem(setValues, null, newValue)}
						>
							Add Value
						</Button>
					</Space>
				</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: TIMELINE                                                     */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab={`Timeline (${timeline.length})`} key="timeline">
					<Space direction="vertical" style={{ width: '100%' }} size="middle">
						{timeline.map((entry, idx) => (
							<Card
								key={idx}
								title={`Timeline Entry ${idx + 1}`}
								extra={
									<Button
										danger
										icon={<DeleteOutlined />}
										size="small"
										onClick={() => removeRepeaterItem(idx, setTimeline, setTimelineImages)}
										disabled={timeline.length <= 1}
									>
										Remove
									</Button>
								}
							>
								<Row gutter={16}>
									<Col span={24} md={4}>
										<div className="form-field-block">
											<label className="input-fields-label">Year</label>
											<Input
												className="input-fields"
												placeholder="e.g. 2015"
												value={entry.year}
												onChange={(e) =>
													updateRepeaterField(idx, 'year', e.target.value, setTimeline)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={8}>
										<div className="form-field-block">
											<label className="input-fields-label">Label</label>
											<Input
												className="input-fields"
												placeholder="Short label"
												value={entry.label}
												onChange={(e) =>
													updateRepeaterField(idx, 'label', e.target.value, setTimeline)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={12}>
										<div className="form-field-block">
											<label className="input-fields-label">Description</label>
											<Input.TextArea
												className="input-fields"
												rows={2}
												placeholder="Timeline entry description"
												value={entry.description}
												onChange={(e) =>
													updateRepeaterField(idx, 'description', e.target.value, setTimeline)
												}
											/>
										</div>
									</Col>
									<Col span={24}>
										<div className="form-field-block">
											<label className="input-fields-label">Entry Image (optional)</label>
											<MediaUpload
												folder={MediaFoldersNames.misc}
												accept="IMAGE"
												media={timelineImages[idx]}
												height="160px"
												maxWidth={480}
												objectFit="cover"
												placeholderText="Optional image for this timeline entry"
												onMediaUploaded={(m) =>
													setTimelineImages((prev) =>
														prev.map((im, i) => (i === idx ? m : im)),
													)
												}
											/>
										</div>
									</Col>
								</Row>
							</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							className="btn-main"
							onClick={() => addRepeaterItem(setTimeline, setTimelineImages, newTimeline)}
						>
							Add Timeline Entry
						</Button>
					</Space>
				</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: EDITORS / TEAM                                               */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab={`Editors (${editors.length})`} key="editors">
					<Space direction="vertical" style={{ width: '100%' }} size="middle">
						{editors.map((editor, idx) => (
							<Card
								key={idx}
								title={`Team Member ${idx + 1}`}
								extra={
									<Button
										danger
										icon={<DeleteOutlined />}
										size="small"
										onClick={() => removeRepeaterItem(idx, setEditors, setEditorPhotos)}
										disabled={editors.length <= 1}
									>
										Remove
									</Button>
								}
							>
								<Row gutter={16}>
									<Col span={24} md={12}>
										<div className="form-field-block">
											<label className="input-fields-label">Photo</label>
											<MediaUpload
												folder={MediaFoldersNames.misc}
												accept="IMAGE"
												media={editorPhotos[idx]}
												height="220px"
												maxWidth={360}
												objectFit="cover"
												placeholderText="Upload team member photo"
												onMediaUploaded={(m) =>
													setEditorPhotos((prev) =>
														prev.map((ph, i) => (i === idx ? m : ph)),
													)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={12}>
										<div className="form-field-block">
											<label className="input-fields-label">Full Name</label>
											<Input
												className="input-fields"
												placeholder="e.g. Ahmed Al Rashid"
												value={editor.name}
												onChange={(e) =>
													updateRepeaterField(idx, 'name', e.target.value, setEditors)
												}
											/>
										</div>
									<div className="form-field-block">
										<label className="input-fields-label">Position / Role</label>
										<Input
											className="input-fields"
											placeholder="e.g. Chief Editor"
											value={editor.position}
											onChange={(e) =>
												updateRepeaterField(idx, 'position', e.target.value, setEditors)
											}
										/>
									</div>
									<div className="form-field-block">
										<label className="input-fields-label">Bio</label>
										<Input.TextArea
											className="input-fields"
											placeholder="Short bio about this team member"
											rows={3}
											value={editor.bio ?? ''}
											onChange={(e) =>
												updateRepeaterField(idx, 'bio', e.target.value, setEditors)
											}
										/>
									</div>
								</Col>
							</Row>
						</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							className="btn-main"
							onClick={() => addRepeaterItem(setEditors, setEditorPhotos, newEditor)}
						>
							Add Team Member
						</Button>
					</Space>
				</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: CITIES                                                       */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab={`Cities Grid (${cities.length})`} key="cities">
					<Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
						Partner and sister-site cities shown in the &quot;Explore Our Other Cities&quot; grid.
						Each entry can differ per region.
					</Text>
					<Space direction="vertical" style={{ width: '100%' }} size="middle">
						{cities.map((city, idx) => (
							<Card
								key={idx}
								title={`City ${idx + 1}`}
								extra={
									<Button
										danger
										icon={<DeleteOutlined />}
										size="small"
										onClick={() => {
											removeRepeaterItem(idx, setCities, setCityBgImages);
											setCityLogos((prev) => prev.filter((_, i) => i !== idx));
										}}
										disabled={cities.length <= 1}
									>
										Remove
									</Button>
								}
							>
								<Row gutter={16}>
									<Col span={24} md={12}>
										<div className="form-field-block">
											<label className="input-fields-label">City Name</label>
											<Input
												className="input-fields"
												placeholder="e.g. AbudhabiNight"
												value={city.name}
												onChange={(e) =>
													updateRepeaterField(idx, 'name', e.target.value, setCities)
												}
											/>
										</div>
										<div className="form-field-block">
											<label className="input-fields-label">Link URL</label>
											<Input
												className="input-fields"
												placeholder="https://abudhabinight.com/"
												value={city.url}
												onChange={(e) =>
													updateRepeaterField(idx, 'url', e.target.value, setCities)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={6}>
										<div className="form-field-block">
											<label className="input-fields-label">Background Image</label>
											<MediaUpload
												folder={MediaFoldersNames.misc}
												accept="IMAGE"
												media={cityBgImages[idx]}
												height="160px"
												maxWidth={280}
												objectFit="cover"
												placeholderText="City card background"
												onMediaUploaded={(m) =>
													setCityBgImages((prev) =>
														prev.map((bg, i) => (i === idx ? m : bg)),
													)
												}
											/>
										</div>
									</Col>
									<Col span={24} md={6}>
										<div className="form-field-block">
											<label className="input-fields-label">Logo</label>
											<MediaUpload
												folder={MediaFoldersNames.misc}
												accept="IMAGE"
												media={cityLogos[idx]}
												height="160px"
												maxWidth={280}
												objectFit="cover"
												placeholderText="City logo"
												onMediaUploaded={(m) =>
													setCityLogos((prev) =>
														prev.map((lg, i) => (i === idx ? m : lg)),
													)
												}
											/>
										</div>
									</Col>
								</Row>
							</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							className="btn-main"
							onClick={() => {
								addRepeaterItem(setCities, setCityBgImages, newCity);
								setCityLogos((prev) => [...prev, undefined]);
							}}
						>
							Add City
						</Button>
					</Space>
				</TabPane>

				{/* ---------------------------------------------------------------- */}
				{/* TAB: SEO                                                          */}
				{/* ---------------------------------------------------------------- */}
				<TabPane tab="SEO" key="seo">
					<Card>
						<Form form={form} component={false}>
							<div className="form-field-block">
								<label className="input-fields-label">Meta Title</label>
								<Form.Item name="metaTitle">
									<Input
										className="input-fields"
										placeholder="About Us — DubaiNight"
									/>
								</Form.Item>
							</div>
							<div className="form-field-block">
								<label className="input-fields-label">Meta Description</label>
								<Form.Item name="metaDescription">
									<Input.TextArea
										className="input-fields"
										rows={4}
										placeholder="Brief description for search engines (max 160 chars)"
										maxLength={160}
										showCount
									/>
								</Form.Item>
							</div>
						</Form>
					</Card>
				</TabPane>
			</Tabs>

			<Divider />
			<Row justify="end">
				<Button
					className="btn-main save"
					loading={saving}
					onClick={handleSave}
					size="large"
				>
					Save All Changes
				</Button>
			</Row>
		</>
	);
};

export default AboutUsEditor;
